import { Link } from 'react-router-dom';
import { Check, FileSpreadsheet } from 'lucide-react';
import SEO from '@/components/common/SEO';
import { ObangsaekStripe } from '@/components/editorial';
import { PLAYBOOKS } from '@/content/financial/playbooks';
import { getCaseStudyBySlug } from '@/content/financial/case-studies';

/**
 * The Playbook Vault.
 *
 * Every asset is `assetPath: null` — these are specified, not yet produced.
 * The page says that plainly instead of rendering a download button that would
 * 404, which is the only honest treatment. When the documents exist, give
 * `assetPath` a value and add the link here; nothing else needs to change.
 */
export default function Playbooks() {
  return (
    <div className="bg-white dark:bg-surface-dark">
      <SEO
        title="Playbooks — Execution Blueprints"
        description="Diligence checklists, term-sheet templates, and workbooks that operationalise the case studies: route-business evaluation, seller-financing negotiation, SDE diligence, and solo entity setup."
        path="/financial/playbooks"
      />

      <header className="border-b border-ink-200 bg-ink-50 dark:border-surface-dark-border dark:bg-white/[0.02]">
        <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6">
          <nav aria-label="Breadcrumb" className="mb-3 text-sm text-ink-500 dark:text-ink-400">
            <Link to="/financial" className="hover:underline">
              Financial
            </Link>
            <span className="mx-2" aria-hidden="true">
              /
            </span>
            <span className="text-ink-700 dark:text-ink-200">Playbooks</span>
          </nav>
          <h1 className="text-3xl font-bold leading-tight text-ink-900 dark:text-ink-50 sm:text-4xl">
            Playbooks
          </h1>
          <p lang="ko" className="mt-1 font-korean text-lg text-ink-500 dark:text-ink-400">
            실행 가이드
          </p>
          <p className="mt-4 max-w-2xl text-base leading-7 text-ink-700 dark:text-ink-200">
            Execution blueprints that turn a case study into a sequence you can actually run. Each
            one is specified below — contents, length, and the teardown it operationalises.
          </p>
        </div>
      </header>

      <ObangsaekStripe />

      <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
        <div
          role="status"
          className="mb-8 rounded-xl border border-cheong-500/30 bg-cheong-500/5 p-5 text-sm leading-relaxed text-ink-700 dark:text-ink-200"
        >
          <strong className="font-semibold text-ink-900 dark:text-ink-50">
            The documents are still in production.
          </strong>{' '}
          Every playbook below is specified but not yet published as a file, so there is nothing to
          download today. These were previously gated behind a paid tier; that tier is gone, and all
          of them will be free when they ship.
        </div>

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          {PLAYBOOKS.map((pb) => {
            const related = pb.relatedCaseStudySlugs
              .map((s) => getCaseStudyBySlug(s))
              .filter((s): s is NonNullable<typeof s> => Boolean(s));

            return (
              <article
                key={pb.slug}
                className="flex flex-col rounded-xl border border-ink-200 bg-white p-5 shadow-sm dark:border-surface-dark-border dark:bg-surface-dark-card sm:p-6"
              >
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-hwang-600 dark:text-hwang-500">
                    <FileSpreadsheet className="h-3 w-3" aria-hidden="true" />
                    {pb.format}
                  </span>
                  <span className="text-[11px] text-ink-400">{pb.pages} pages</span>
                  <span className="rounded-full bg-green-50 px-2 py-0.5 text-[11px] font-medium text-green-700 dark:bg-green-900/20 dark:text-green-500">
                    Free
                  </span>
                </div>

                <h2 className="mt-2.5 text-lg font-bold leading-snug text-ink-900 dark:text-ink-50">
                  {pb.title}
                </h2>
                <p className="mt-2 text-sm leading-6 text-ink-500 dark:text-ink-400">
                  {pb.summary}
                </p>

                <h3 className="mt-4 text-[11px] font-bold uppercase tracking-wide text-ink-400">
                  What's inside
                </h3>
                <ul className="mt-2 flex-1 space-y-1.5">
                  {pb.includes.map((item) => (
                    <li
                      key={item}
                      className="flex gap-2 text-sm leading-6 text-ink-700 dark:text-ink-200"
                    >
                      <Check
                        className="mt-1.5 h-3 w-3 shrink-0 text-green-600 dark:text-green-500"
                        aria-hidden="true"
                      />
                      {item}
                    </li>
                  ))}
                </ul>

                {related.length > 0 && (
                  <div className="mt-4 border-t border-ink-200 pt-3 dark:border-surface-dark-border">
                    <p className="text-[11px] font-bold uppercase tracking-wide text-ink-400">
                      Operationalises
                    </p>
                    <ul className="mt-1.5 space-y-1">
                      {related.map((study) => (
                        <li key={study.slug}>
                          <Link
                            to={`/financial/case-studies/${study.slug}`}
                            className="text-sm font-medium text-cheong-500 hover:underline dark:text-cheong-dark"
                          >
                            {study.title}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <p className="mt-4 rounded-lg bg-ink-100 px-3 py-2 text-xs text-ink-500 dark:bg-white/[0.06] dark:text-ink-400">
                  Not yet available for download.
                </p>
              </article>
            );
          })}
        </div>
      </div>
    </div>
  );
}
